import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';

import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { account, avatars } from '@/lib/appwrite/config';
import { saveUserToDB } from '@/lib/appwrite/api';
import { INewUser } from '@/types';

const verificationSchema = z.object({
  otp: z.string().min(4, 'OTP must be at least 4 characters'),
});

type VerificationData = {
  user: INewUser;
  userId: string;
  sessionId: string;
};

const VerifyPhone = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [verificationData, setVerificationData] = useState<VerificationData | null>(null);
  const [resendTimer, setResendTimer] = useState(60);

  const form = useForm<z.infer<typeof verificationSchema>>({
    resolver: zodResolver(verificationSchema),
    defaultValues: {
      otp: '',
    },
  });

  useEffect(() => {
    // Retrieve verification data from sessionStorage
    const storedData = sessionStorage.getItem('phoneVerification');
    if (!storedData) {
      toast({ 
        title: "Verification Error",
        description: "No verification data found. Please sign up again.",
        variant: "destructive"
      });
      navigate('/sign-up');
      return;
    }
    setVerificationData(JSON.parse(storedData));

    // Set up resend timer
    const timer = setInterval(() => {
      setResendTimer((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);

    return () => clearInterval(timer);
  }, [navigate, toast]);

  const handleVerification = async (values: z.infer<typeof verificationSchema>) => {
    if (!verificationData) return;

    setIsLoading(true);
    try {
      // Implement your verification logic here using Appwrite
      // Example:
      await account.createSession(
        verificationData.userId,
        values.otp
      );
      const user = verificationData.user;
      const formattedPhone = user.emailOrPhone.startsWith('+91') 
            ? user.emailOrPhone 
            : `+91${user.emailOrPhone}`;
      const tempEmail = `${formattedPhone}@busiconn.com`;
      // Create the account with phone number
                const avatarUrl = avatars.getInitials(user.name);
                const newUser = await saveUserToDB({
                  accountId: verificationData.userId,
                  name: user.name,
                  emailOrPhone: tempEmail,  //test later whether it works when you replace this with formattedPhone
                  username: user.username,
                  acceptedSLA: user.acceptedSLA,
                  imageUrl: avatarUrl,
                });
                if(!newUser){
                  console.log("user not created into database");
                }
      toast({
        title: "Verification Successful!",
        description: "YWelcome to busiconn, happy journey.",
      });

      
      const updateEmail = account.updateEmail(tempEmail, user.password);
      const updateName = account.updateName(user.name);
      if(!updateEmail && !updateName){ console.log("password not updated")}
      // Clear verification data
      sessionStorage.removeItem('phoneVerification');

      navigate('/');
    } catch (error) {
      console.error('Verification Error:', error);
      toast({ 
        title: "Verification Failed",
        description: error instanceof Error ? error.message : "Please try again",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (!verificationData || resendTimer > 0) return;

    setIsLoading(true);
    try {
      // Implement resend logic here using Appwrite
      // Example:
      await account.createPhoneToken(
        verificationData.userId,
        `+91${verificationData.user.emailOrPhone}`
      );

      setResendTimer(60);
      toast({
        title: "Code Resent",
        description: "A new verification code has been sent to your phone.",
      });
    } catch (error) {
      console.error('Resend Error:', error);
      toast({ 
        title: "Failed to resend code",
        description: error instanceof Error ? error.message : "Please try again",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex-center flex-col min-h-screen">
      <div className="sm:w-420 flex-center flex-col">
        <h2 className="h3-bold md:h2-bold">Phone Verification</h2>
        <p className="text-light-3 small-medium md:base-regular mt-2 text-center">
          Enter the verification code sent to{' '}
          <span className="font-semibold">{verificationData?.user.emailOrPhone}</span>
        </p>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleVerification)} className="flex flex-col gap-5 w-full mt-4">
            <FormField
              control={form.control}
              name="otp"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="shad-form_label">Verification Code</FormLabel>
                  <FormControl>
                    <Input
                      type="text"
                      className="shad-input text-center text-2xl tracking-widest"
                      maxLength={6}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Button 
              type="submit" 
              className="shad-button_primary"
              disabled={isLoading}
            >
              {isLoading ? "Verifying..." : "Verify"}
            </Button>
          </form>
        </Form>

        <div className="mt-4 text-center">
          <p className="text-light-3 text-small-regular">
            Didn't receive the code?{' '}
            {resendTimer > 0 ? (
              <span className="text-primary-500">
                Resend in {resendTimer}s
              </span>
            ) : (
              <Button
                variant="link"
                className="text-primary-500 p-0"
                onClick={handleResendCode}
                disabled={isLoading}
              >
                Resend Code
              </Button>
            )}
          </p>
        </div>
      </div>
    </div>
  );
};

export default VerifyPhone;